import { memo, useState, useCallback, useRef, useEffect } from 'react'
import { useNavigate } from './nav'
import { ExternalLink, Pause, Play, Trash2, Share2 } from 'lucide-react'
import type { Project } from '../data/projectTypes'
import { useDeleteProjectMutation, useGetProcessingSummaryQuery, useGetProjectExplorationsQuery, useUpdateProjectMutation } from '../services/projectDataApi'
import ShareDialog from './ShareDialog'
import SharedBadge from './SharedBadge'
import { useShareDialog } from '../lib/useShareDialog'
import './ProjectHeader.css'

interface ProjectHeaderProps {
  project: Project
  readOnly?: boolean
  // True when the viewer is a member of (not the owner of) this project — someone else's project
  // shared with them. Drives the "Shared" cue next to the title. Resolved from the members query in
  // ProjectDetail rather than the project row, since the single-project GET carries no viewer role.
  shared?: boolean
}

export default memo(function ProjectHeader({ project, readOnly, shared }: ProjectHeaderProps) {
  const [deleteProject] = useDeleteProjectMutation()
  const [updateProjectQuery] = useUpdateProjectMutation()
  const navigate = useNavigate()
  const [showShare, setShowShare] = useShareDialog()
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [editingName, setEditingName] = useState(false)
  const isActive = project.status === 'active'
  // A run is in progress while the project has a running task. The sidebar polls
  // this set globally, so it stays current regardless of which tab is open.
  const processingQuery = useGetProcessingSummaryQuery(undefined, { pollingInterval: 10_000, skipPollingIfUnfocused: true })
  const runInProgress = processingQuery.data?.running.some((task) => task.project_id === project.id) ?? false
  // Sharing is only meaningful once there are results to show, so the Share button stays disabled
  // until the project's initial analysis (its first exploration) finishes. Mirrors OverviewTab's
  // `inSetup`: a first exploration exists but none has completed or failed yet. The explorations
  const { data: explorations } = useGetProjectExplorationsQuery({ projectId: project.id, page: 1, pageSize: 100 })
  const initialAnalysisPending = Boolean(
    explorations && explorations.total > 0 && !explorations.items.some(e => e.status === 'completed' || e.status === 'failed'),
  )
  const [nameValue, setNameValue] = useState(project.name)
  const nameInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (editingName) nameInputRef.current?.select()
  }, [editingName])

  const startEditName = useCallback(() => {
    if (readOnly) return
    setNameValue(project.name)
    setEditingName(true)
  }, [readOnly, project.name])

  const saveName = useCallback(() => {
    const trimmed = nameValue.trim()
    setEditingName(false)
    if (!trimmed || trimmed === project.name) return
    void updateProjectQuery({ id: project.id, data: { name: trimmed } })
  }, [nameValue, project.id, project.name, updateProjectQuery])

  const handleNameKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') saveName()
    else if (e.key === 'Escape') setEditingName(false)
  }, [saveName])

  const handleTogglePause = useCallback(() => {
    void updateProjectQuery({
      id: project.id,
      data: { action: 'update-status', status: isActive ? 'paused' : 'active' },
    })
  }, [project.id, isActive, updateProjectQuery])

  async function handleDelete() {
    setDeleting(true)
    try {
      await deleteProject(project.id).unwrap()
      navigate('/')
    } catch {
      setDeleting(false)
    }
  }

  return (
    <header className="project-header" data-testid="project-header">
      <div className="project-header__info">
        {editingName ? (
          <input
            ref={nameInputRef}
            className="project-header__name-input"
            value={nameValue}
            onChange={e => setNameValue(e.target.value)}
            onBlur={saveName}
            onKeyDown={handleNameKeyDown}
            data-testid="project-name-input"
            data-analytics-event="project-header-name-input"
          />
        ) : (
          <h1
            className={`project-header__name${readOnly ? '' : ' project-header__name--editable'}`}
            data-testid="project-name"
            data-analytics-event="project-header-name-edit"
            onClick={startEditName}
            onKeyDown={e => { if (!readOnly && (e.key === 'Enter' || e.key === ' ')) startEditName() }}
            tabIndex={readOnly ? undefined : 0}
            role={readOnly ? undefined : 'button'}
            aria-label={readOnly ? undefined : 'Click to edit project name'}
          >
            {project.name}
          </h1>
        )}
        {shared && <SharedBadge />}
        {/* GitHub-synced projects have no target_url — fall back to the repo URL so the header still
            links somewhere meaningful, and render nothing when there's neither. */}
        {(project.target_url || project.github?.repoUrl) && (
          <a
            className="project-header__url"
            href={project.target_url || project.github?.repoUrl || undefined}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="project-url"
            data-analytics-event="project-header-target-link"
            aria-label={`Visit ${project.target_url || project.github?.repoUrl} (opens in new tab)`}
          >
            <ExternalLink size={14} aria-hidden="true" />
            {project.target_url || project.github?.repoUrl}
          </a>
        )}
      </div>
      {!readOnly && (
        <div className="project-header__actions">
          <button
            className="project-header__share-btn"
            onClick={() => setShowShare(true)}
            disabled={initialAnalysisPending}
            title={initialAnalysisPending ? 'Sharing will be available once the initial analysis completes' : undefined}
            data-testid="share-project-btn"
            data-analytics-event="project-header-share-button"
          >
            <Share2 size={14} aria-hidden="true" />
            Share
          </button>
          {runInProgress ? (
            <button
              className="project-header__pause-btn"
              onClick={handleTogglePause}
              data-testid="pause-project-btn"
              data-analytics-event="project-header-pause-toggle"
              aria-label={isActive ? 'Pause project' : 'Resume project'}
            >
              {isActive ? <Pause size={14} aria-hidden="true" /> : <Play size={14} aria-hidden="true" />}
              {isActive ? 'Pause' : 'Resume'}
            </button>
          ) : !showDeleteConfirm ? (
            <button
              className="project-header__delete-btn"
              onClick={() => setShowDeleteConfirm(true)}
              data-testid="delete-project-btn"
              data-analytics-event="project-header-delete-button"
            >
              <Trash2 size={14} aria-hidden="true" />
              Delete
            </button>
          ) : (
            <div className="project-header__delete-confirm" data-testid="delete-confirm">
              <span>Delete this project?</span>
              <button
                className="project-header__confirm-delete-btn"
                onClick={handleDelete}
                disabled={deleting}
                data-testid="confirm-delete-btn"
                data-analytics-event="project-header-delete-confirm"
              >
                {deleting ? 'Deleting...' : 'Confirm'}
              </button>
              <button
                className="project-header__cancel-delete-btn"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                data-analytics-event="project-header-delete-cancel"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}
      {showShare && <ShareDialog project={project} readOnly={readOnly} onClose={() => setShowShare(false)} />}
    </header>
  )
})
